import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { apiClient, WabaAccount } from '../services/api';
import { Phone, Plus, Power, RefreshCw, Star, Users, Clock, ArrowLeft, Gauge } from 'lucide-react';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import { formatDateBR, formatDateTimeBR } from '../utils/dateUtils';
import { getQualityRatingColor, getQualityRatingLabel, getAccountStatusColor, getAccountStatusLabel, formatTier } from '../utils/statusColors';
import ConfirmDialog, { useConfirmDialog } from '../components/ui/ConfirmDialog';

export default function WabaAccounts() {
  const { accessToken, logout, isAuthenticated, isLoading: authLoading, user } = useAuth();
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState<WabaAccount[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [isFetching, setIsFetching] = useState(false);
  const { confirm: showConfirm, dialogProps } = useConfirmDialog();

  // Check if user is admin (to show back to dashboard button)
  const isAdmin = user?.role === 'super_admin' || user?.role === 'admin';

  // Redirecionar para login se não estiver autenticado
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login');
    }
  }, [authLoading, isAuthenticated, navigate]);

  const fetchAccounts = async () => {
    // Prevenir chamadas múltiplas simultâneas
    if (isFetching) {
      return;
    }

    if (!accessToken) {
      setIsLoading(false);
      return;
    }

    try {
      setIsFetching(true);
      setIsLoading(true);
      setError(null);
      const response = await apiClient.getWabaAccounts(accessToken);

      if (response.success) {
        setAccounts(response.data.wabaAccounts || []);
      } else {
        setError(response.message || 'Falha ao carregar contas WABA');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erro ao carregar contas WABA';
      setError(errorMessage);

      // Se for erro de autenticação, fazer logout
      if (errorMessage.includes('Sessão expirada') || errorMessage.includes('Token inválido') || errorMessage.includes('autenticação')) {
        setTimeout(() => {
          logout();
          navigate('/login');
        }, 2000);
      }
    } finally {
      setIsFetching(false);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    // Esperar o contexto de auth carregar antes de buscar dados
    if (!authLoading && accessToken) {
      fetchAccounts();
    } else if (!authLoading && !accessToken) {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken, authLoading]);

  const handleToggleStatus = async (id: string, currentStatus: boolean) => {
    if (!accessToken) return;

    try {
      setTogglingId(id);
      const response = await apiClient.updateWabaStatus(
        id,
        { isActive: !currentStatus },
        accessToken
      );

      if (response.success) {
        setAccounts((prev) =>
          prev.map((acc) => (acc.id === id ? response.data : acc))
        );
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao atualizar status');
    } finally {
      setTogglingId(null);
    }
  };


  return (
    <div className="min-h-screen bg-bg-1">
      <Header
        title="Contas WhatsApp Business"
        subtitle="Gerencie suas contas conectadas ao Dialog360"
      />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back button - only for admins */}
        {isAdmin && (
          <button
            onClick={() => navigate('/admin')}
            className="flex items-center text-text-2 hover:text-text-1 mb-4 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Voltar ao Dashboard
          </button>
        )}

        {/* Actions Bar */}
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center gap-2">
            <Phone className="text-accent-1" size={24} />
            <span className="text-text-2 font-medium">
              {accounts.length} {accounts.length === 1 ? 'conta' : 'contas'} conectada{accounts.length !== 1 ? 's' : ''}
            </span>
          </div>
          <div className="flex gap-3">
            <button
              onClick={fetchAccounts}
              disabled={isLoading}
              className="flex items-center gap-2 px-4 py-2 bg-bg-2 border border-bg-3 text-text-1 rounded-md hover:bg-bg-3 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <RefreshCw size={18} className={isLoading ? 'animate-spin' : ''} />
              Atualizar
            </button>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 bg-danger/10 border border-danger rounded-md p-4">
            <p className="text-danger text-sm font-medium mb-3">{error}</p>
            {error.includes('Token inválido') || error.includes('Sessão expirada') || error.includes('autenticação') ? (
              <button
                onClick={() => {
                  logout();
                  navigate('/login');
                }}
                className="px-4 py-2 bg-danger hover:bg-danger/90 text-white text-sm rounded-md transition-colors"
              >
                Fazer Login Novamente
              </button>
            ) : null}
          </div>
        )}

        {/* Loading State */}
        {(isLoading || authLoading) && accounts.length === 0 ? (
          <LoadingSpinner size="lg" />
        ) : accounts.length === 0 ? (
          /* Empty State */
          <div className="bg-bg-2 border border-bg-3 rounded-lg shadow-md p-12 text-center">
            <Phone className="mx-auto mb-4 text-text-3" size={48} />
            <h3 className="text-xl font-semibold text-text-1 mb-2">
              Nenhuma conta conectada
            </h3>
            <p className="text-text-2">
              Entre em contato com o administrador do sistema para conectar uma conta WhatsApp Business
            </p>
          </div>
        ) : (
          /* Accounts List */
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {accounts.map((account) => (
              <div
                key={account.id}
                className="bg-bg-2 border border-bg-3 rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow"
              >
                {/* Header com Phone e Status */}
                <div className="flex justify-between items-start mb-4">
                  <div className="flex items-center gap-3">
                    <div
                      className={`w-12 h-12 rounded-full flex items-center justify-center ${
                        account.accountStatus?.toLowerCase() === 'ready' ? 'bg-success/10' : 'bg-bg-3'
                      }`}
                    >
                      <Phone
                        size={24}
                        className={account.accountStatus?.toLowerCase() === 'ready' ? 'text-success' : 'text-text-3'}
                      />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-text-1">
                        {account.displayPhoneNumber}
                      </h3>
                      {account.businessName && (
                        <p className="text-text-2 text-sm font-medium">
                          {account.businessName}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getAccountStatusColor(account.accountStatus)}`}>
                      {getAccountStatusLabel(account.accountStatus)}
                    </span>
                    {account.isActive !== undefined && (
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium ${
                          account.isActive
                            ? 'bg-success/10 text-success'
                            : 'bg-bg-3 text-text-3'
                        }`}
                      >
                        {account.isActive ? 'Ativa' : 'Inativa'}
                      </span>
                    )}
                  </div>
                </div>

                {/* Métricas de Qualidade e Limites */}
                {(account.qualityRating || account.messagingLimitTier || account.maxDailyConversations || (account.dailyDispatchLimit && account.dailyDispatchLimit > 0)) && (
                  <div className="grid grid-cols-2 gap-3 mb-4 p-3 bg-bg-1 rounded-lg border border-border-1">
                    {/* Quality Rating */}
                    {account.qualityRating && (
                      <div className="flex items-start gap-2">
                        <Star size={16} className="text-warning mt-0.5 flex-shrink-0" />
                        <div className="min-w-0">
                          <p className="text-xs text-text-3 mb-1">Qualidade</p>
                          <span className={`inline-block px-2 py-0.5 rounded text-xs font-semibold ${getQualityRatingColor(account.qualityRating)}`}>
                            {getQualityRatingLabel(account.qualityRating)}
                          </span>
                        </div>
                      </div>
                    )}

                    {/* Limite de Disparo (24h) - substitui Limite Diário quando configurado */}
                    {account.dailyDispatchLimit && account.dailyDispatchLimit > 0 ? (
                      <div className="flex items-start gap-2">
                        <Gauge size={16} className="text-success mt-0.5 flex-shrink-0" />
                        <div className="min-w-0">
                          <p className="text-xs text-text-3 mb-1">Disparo (24h)</p>
                          <p className="text-sm font-bold text-text-1">
                            {account.dailyDispatchLimit.toLocaleString()}/dia
                          </p>
                        </div>
                      </div>
                    ) : (account.messagingLimitTier || account.maxDailyConversations) ? (
                      <div className="flex items-start gap-2">
                        <Users size={16} className="text-accent-1 mt-0.5 flex-shrink-0" />
                        <div className="min-w-0">
                          <p className="text-xs text-text-3 mb-1">Limite Diário</p>
                          <p className="text-sm font-bold text-text-1">
                            {account.maxDailyConversations ? `${account.maxDailyConversations.toLocaleString()}` : formatTier(account.messagingLimitTier)}
                          </p>
                        </div>
                      </div>
                    ) : null}

                    {/* Last Quality Check */}
                    {account.lastQualityCheck && (
                      <div className="flex items-start gap-2 col-span-2">
                        <Clock size={16} className="text-accent-2 mt-0.5 flex-shrink-0" />
                        <div className="min-w-0">
                          <p className="text-xs text-text-3 mb-1">Última Verificação</p>
                          <p className="text-xs text-text-2">
                            {formatDateTimeBR(account.lastQualityCheck)}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Phone Number ID e Data de Criação */}
                <div className="mb-4 space-y-2">
                  {/* Phone Number ID */}
                  {account.phoneNumberId && (
                    <div className="flex items-center gap-2 text-sm">
                      <span className="text-text-3">Phone Number ID:</span>
                      <span className="text-text-2 font-mono text-xs bg-bg-3 px-2 py-1 rounded">
                        {account.phoneNumberId}
                      </span>
                    </div>
                  )}

                  {/* Data de Criação */}
                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-text-3">Criada em:</span>
                    <span className="text-text-2">
                      {formatDateBR(account.createdAt)}
                    </span>
                  </div>
                </div>

                <div className="flex flex-col gap-2 pt-4 border-t border-bg-3">
                  <button
                    onClick={() => navigate(`/waba/${account.id}/environment`)}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-accent-1 hover:bg-accent-2 text-white font-medium rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-accent-1 focus:ring-offset-2 focus:ring-offset-bg-2"
                  >
                    Selecionar Ambiente
                  </button>
                  <div>
                    <button
                      onClick={() => handleToggleStatus(account.id, account.isActive)}
                      disabled={togglingId === account.id}
                      className={`w-full flex items-center justify-center gap-2 px-3 py-2 text-sm rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                        account.isActive
                          ? 'bg-warning/10 text-warning hover:bg-warning/20'
                          : 'bg-success/10 text-success hover:bg-success/20'
                      }`}
                    >
                      <Power size={14} />
                      {togglingId === account.id
                        ? 'Processando...'
                        : account.isActive
                          ? 'Desativar'
                          : 'Ativar'}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      <ConfirmDialog {...dialogProps} />
    </div>
  );
}
